import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  THEME_STORAGE_KEY,
  applyResolvedTheme,
  readStoredTheme,
  resolveThemePreference,
} from '../theme/applyTheme';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveThemePreference(readStoredTheme()));

  const applyTheme = useCallback((preference) => {
    const resolved = resolveThemePreference(preference);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
    return resolved;
  }, []);

  const setTheme = useCallback(
    (preference) => {
      if (preference !== 'light' && preference !== 'dark' && preference !== 'system') return;
      setThemeState(preference);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, preference);
      } catch {
        /* ignore */
      }
      applyTheme(preference);
    },
    [applyTheme]
  );

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme, applyTheme]);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
